import {
  Box,
  CircularProgress,
  Divider,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  Stack,
  StatusChip,
  type StatusKind,
  Typography,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import type {
  EntryWarrantDetail,
  EntryWarrantStatus,
  WarrantLogEntry,
} from '@munifocus/contracts-gov';
import { useWarrants } from '../lib/warrant-api';
import { frenchFirstOfMonth } from '@munifocus/utils';

/** Map a warrant lifecycle status onto the design-system StatusChip palette. */
function statusKind(status: EntryWarrantStatus): StatusKind {
  switch (status) {
    case 'granted':
      return 'approved';
    case 'executed':
      return 'success';
    case 'denied':
    case 'closed_extension_denied':
      return 'rejected';
    case 'extension_pending':
    case 'closed_unexecuted':
      return 'warning';
    case 'filed':
    default:
      return 'pending';
  }
}

function formatDateTime(iso: string, locale: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso)),
  );
}

function WarrantLogEntries({ log, locale }: { log: WarrantLogEntry[]; locale: string }) {
  const { t } = useTranslation();
  return (
    <Box
      component="ol"
      aria-label={t('gov.warrant.logTitle')}
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        mt: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      {log.map((entry, i) => (
        <Box component="li" key={`${entry.at}-${i}`} sx={{ display: 'flex', gap: 1.25 }}>
          <Box
            aria-hidden
            sx={{
              mt: '6px',
              width: 7,
              height: 7,
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: palette.brand.accent,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t(`gov.warrant.event.${entry.event}`)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(entry.at, locale)}
            </Typography>
            {entry.note ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {entry.note}
              </Typography>
            ) : null}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="caption" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

function WarrantCard({ warrant, locale }: { warrant: EntryWarrantDetail; locale: string }) {
  const { t } = useTranslation();
  return (
    <HairlineCard sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Icons.Lock size={iconSize.md} color={palette.brand.accent} />
          <Typography variant="subtitle2">
            {t(`gov.warrant.trigger.${warrant.triggerPhase}`)}
          </Typography>
        </Stack>
        <StatusChip
          status={statusKind(warrant.status)}
          label={t(`gov.warrant.status.${warrant.status}`)}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {warrant.applicableStatute}
      </Typography>

      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        {warrant.filedAt ? (
          <MetaRow
            label={t('gov.warrant.filedAt')}
            value={formatDateTime(warrant.filedAt, locale)}
          />
        ) : null}
        {warrant.expiryDate ? (
          <MetaRow
            label={t('gov.warrant.expiryDate')}
            value={formatDateTime(warrant.expiryDate, locale)}
          />
        ) : null}
        {warrant.followUpDueAt ? (
          <MetaRow
            label={t('gov.warrant.followUpDue')}
            value={formatDateTime(warrant.followUpDueAt, locale)}
          />
        ) : null}
      </Stack>

      {warrant.log.length > 0 ? (
        <>
          <Divider sx={{ mt: 1.5 }} />
          <WarrantLogEntries log={warrant.log} locale={locale} />
        </>
      ) : null}
    </HairlineCard>
  );
}

/**
 * Warrant-log widget (brief §W2 / §X4) — the chronological history of every
 * Entry Warrant on a file, rendered alongside the inspection log. Read-only:
 * the filing/decision/execution actions live in the warrant sub-flow screens.
 */
export function WarrantLog({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const { data: warrants, isLoading, isError } = useWarrants(fileId);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Icons.ScrollText size={iconSize.lg} color={palette.brand.accent} />
        <Typography variant="h6">{t('gov.warrant.logTitle')}</Typography>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Typography variant="body2" color="error">
          {t('gov.warrant.loadError')}
        </Typography>
      ) : !warrants || warrants.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('gov.warrant.none')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {warrants.map((w) => (
            <WarrantCard key={w.id} warrant={w} locale={locale} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
